// Handles point-cloud file drop/upload UI and progress feedback states.
import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "dropzone",
    "input",
    "progressSection",
    "progressBar",
    "status",
    "statusDetail",
    "submit"
  ]

  connect() {
    this.fadeOutTimeout = null
    this.resetProgress()
  }

  disconnect() {
    this.clearFadeOutTimeout()
  }

  fileSelected() {
    this.clearFadeOutTimeout()
    this.resetProgress()
  }

  dragOver(event) {
    event.preventDefault()
    this.setDragState(true)
  }

  dragLeave(event) {
    event.preventDefault()
    this.setDragState(false)
  }

  drop(event) {
    event.preventDefault()
    this.setDragState(false)

    const files = event.dataTransfer.files
    if (!files || files.length === 0) {
      return
    }

    // Build a synthetic FileList so the native file input receives dropped files.
    const dataTransfer = new DataTransfer()
    Array.from(files).forEach((file) => dataTransfer.items.add(file))
    this.inputTarget.files = dataTransfer.files
    this.fileSelected()
  }

  submitStart() {
    if (!this.inputTarget.files.length) {
      return
    }

    // Keep this progress optimistic until backend conversion tracking takes over.
    this.showProgressSection()
    this.updateProgress(35)
    this.statusTarget.textContent = "Uploading"
    this.statusDetailTarget.textContent = "Uploading file..."
    this.statusTarget.classList.remove("text-bg-success", "text-bg-danger")
    this.statusTarget.classList.add("text-bg-secondary")
    this.progressBarTarget.classList.add("progress-bar-striped", "progress-bar-animated")
    this.submitTarget.disabled = true
  }

  submitEnd(event) {
    if (!this.inputTarget.files.length) {
      return
    }

    if (event.detail.success) {
      this.updateProgress(100)
      this.statusTarget.textContent = "Uploaded"
      this.statusDetailTarget.textContent = "Upload complete."
      this.statusTarget.classList.remove("text-bg-secondary", "text-bg-danger")
      this.statusTarget.classList.add("text-bg-success")
      this.progressBarTarget.classList.remove("progress-bar-striped", "progress-bar-animated")
      this.inputTarget.value = ""
      this.fadeOutProgressSection()
    } else {
      this.updateProgress(0)
      this.statusTarget.textContent = "Upload failed"
      this.statusDetailTarget.textContent = "Please check the file and try again."
      this.statusTarget.classList.remove("text-bg-secondary", "text-bg-success")
      this.statusTarget.classList.add("text-bg-danger")
      this.progressBarTarget.classList.remove("progress-bar-striped", "progress-bar-animated")
    }

    this.submitTarget.disabled = false
  }

  setDragState(active) {
    this.dropzoneTarget.classList.toggle("border-primary", active)
    this.dropzoneTarget.classList.toggle("bg-light", active)
  }

  showProgressSection() {
    this.progressSectionTarget.classList.remove("d-none")
    this.progressSectionTarget.style.transition = ""
    this.progressSectionTarget.style.opacity = "1"
  }

  updateProgress(value) {
    const safeValue = Math.min(100, Math.max(0, value))
    this.progressBarTarget.style.width = `${safeValue}%`
    this.progressBarTarget.textContent = `${safeValue}%`
    this.progressBarTarget.setAttribute("aria-valuenow", safeValue)
  }

  resetProgress() {
    this.progressSectionTarget.classList.add("d-none")
    this.progressSectionTarget.style.transition = ""
    this.progressSectionTarget.style.opacity = "1"
    this.updateProgress(0)
    this.statusTarget.textContent = "Waiting"
    this.statusDetailTarget.textContent = "Ready to upload."
    this.statusTarget.classList.remove("text-bg-success", "text-bg-danger")
    this.statusTarget.classList.add("text-bg-secondary")
    this.progressBarTarget.classList.remove("progress-bar-striped", "progress-bar-animated")
  }

  fadeOutProgressSection() {
    this.clearFadeOutTimeout()

    // Keep success feedback visible long enough before resetting for the next upload.
    this.fadeOutTimeout = setTimeout(() => {
      this.progressSectionTarget.style.transition = "opacity 900ms ease"
      this.progressSectionTarget.style.opacity = "0"

      this.fadeOutTimeout = setTimeout(() => {
        this.resetProgress()
      }, 900)
    }, 3500)
  }

  clearFadeOutTimeout() {
    if (this.fadeOutTimeout) {
      clearTimeout(this.fadeOutTimeout)
      this.fadeOutTimeout = null
    }
  }

}
